import React, { useState, useEffect } from "react";
import { Box, Button, Container, Dialog, DialogTitle, DialogContent, DialogActions, TextField, TextareaAutosize, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Alert } from "@mui/material";
import { Edit as EditIcon, Delete as DeleteIcon } from "@mui/icons-material";

import { apiService } from "../../utils/api";

const PromptManagement = () => {
  const [prompts, setPrompts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    content: "",
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const fetchPrompts = async () => {
    try {
      const response = await apiService.getPromtList();
      setPrompts(response.data.data);
    } catch (err) {
      setError("Failed to fetch prompts");
    }
  };

  useEffect(() => {
    fetchPrompts();
  }, []);

  const handleOpen = (prompt = null) => {
    if (prompt) {
      setEditingPrompt(prompt);
      setFormData({
        title: prompt.title,
        content: prompt.content,
      });
    } else {
      setEditingPrompt(null);
      setFormData({
        title: "",
        content: "",
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingPrompt(null);
    setFormData({
      title: "",
      content: "",
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPrompt) {
        await apiService.updatePrompt(`${editingPrompt.id}`, formData);
        setSuccess("Prompt updated successfully");
      } else {
        await apiService.addPrompt(formData);
        setSuccess("Prompt created successfully");
      }
      handleClose();
      fetchPrompts();
    } catch (err) {
      setError(err.response?.data?.error || "Operation failed");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this prompt?")) {
      try {
        await apiService.delete(`${id}`);
        setSuccess("Prompt deleted successfully");
        fetchPrompts();
      } catch (err) {
        setError("Failed to delete prompt");
      }
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Typography variant="h4" component="h1">
          Prompt Management
        </Typography>
        <Button variant="contained" color="primary" onClick={() => handleOpen()}>
          Add New Prompt
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Content</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {prompts.map((prompt) => (
              <TableRow key={prompt.id}>
                <TableCell>{prompt.title}</TableCell>
                <TableCell>{prompt.content}</TableCell>
                <TableCell>
                  <IconButton color="primary" onClick={() => handleOpen(prompt)}>
                    <EditIcon />
                  </IconButton>
                  {/* <IconButton color="error" onClick={() => handleDelete(prompt.id)}>
                    <DeleteIcon />
                  </IconButton> */}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>{editingPrompt ? "Edit Prompt" : "Create New Prompt"}</DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <TextField fullWidth label="Title" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} margin="normal" required disabled />
            <TextareaAutosize fullWidth label="Content" value={formData.content} onChange={(e) => setFormData({ ...formData, content: e.target.value })} margin="normal" required multiline rows={4} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {editingPrompt ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PromptManagement;
